/**
 * AdminCorneliusEntities — Tier-2 entity graph viewer
 *
 * Lists the top entities Cornelius has extracted from a user's memory stream
 * (people / orgs / projects / dates / places / money / urls), grouped by
 * type, with click-through to the memories that mention each one.
 *
 * Styling follows the OPO admin convention: var(--card-bg), var(--primary-color),
 * etc. so it stays in lockstep with the dark/light theme.
 */

import React, { useState, useEffect, useCallback } from 'react';

const TYPE_LABELS = {
  person:  'People',
  org:     'Organizations',
  project: 'Projects',
  date:    'Dates',
  place:   'Places',
  money:   'Amounts',
  url:     'URLs',
  other:   'Other',
};

const TYPE_ORDER = ['person','org','project','date','place','money','url','other'];

const TYPE_COLOR = {
  person:  'var(--primary-color)',
  org:     'var(--success-color)',
  project: 'var(--warning-color)',
  date:    'var(--text-secondary)',
  place:   'var(--primary-color)',
  money:   'var(--success-color)',
  url:     'var(--text-secondary)',
  other:   'var(--text-secondary)',
};

const card = (selected) => ({
  background:   selected ? 'rgba(60,252,249,0.06)' : 'var(--card-bg)',
  border:       `1px solid ${selected ? 'var(--primary-color)' : 'var(--border-color)'}`,
  borderRadius: 'var(--border-radius)',
  padding:      '10px 12px',
  marginBottom: 8,
  color:        'var(--text-primary)',
  boxShadow:    'var(--shadow-sm)',
  cursor:       'pointer',
});

const input = {
  padding: '7px 12px', borderRadius: 'var(--border-radius)', border: '1px solid var(--border-color)',
  background: 'var(--surface-color)', color: 'var(--text-primary)', fontSize: 13, outline: 'none',
};

const btn = {
  padding: '7px 14px', borderRadius: 'var(--border-radius)', border: 'none',
  background: 'var(--primary-color)', color: 'var(--text-on-primary)',
  fontSize: 13, fontWeight: 600, cursor: 'pointer',
};

function Pill({ color, children }) {
  return (
    <span style={{
      padding: '2px 7px', borderRadius: 10, fontSize: 11, fontWeight: 600,
      background: 'var(--surface-color)', border: '1px solid var(--border-color)',
      color: color || 'var(--text-secondary)',
    }}>{children}</span>
  );
}

function EntityRow({ entity, selected, onClick }) {
  return (
    <div style={card(selected)} onClick={onClick}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <Pill color={TYPE_COLOR[entity.entity_type]}>{entity.entity_type}</Pill>
        <span style={{ fontWeight: 600, color: 'var(--text-primary)', flex: 1, minWidth: 0, wordBreak: 'break-word' }}>
          {entity.display_name}
        </span>
        <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
          {entity.mention_count} mention{entity.mention_count === 1 ? '' : 's'}
        </span>
        <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
          last {entity.last_seen ? new Date(entity.last_seen).toLocaleDateString() : '—'}
        </span>
      </div>
    </div>
  );
}

function MemoryRow({ m }) {
  return (
    <div style={{
      background: 'var(--surface-color)', border: '1px solid var(--border-color)',
      borderRadius: 'var(--border-radius)', padding: '8px 10px', marginBottom: 6,
      fontSize: 12, color: 'var(--text-primary)',
    }}>
      <div style={{ display: 'flex', gap: 8, fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>
        <span>{m.memory_type}</span>
        <span>imp {m.importance}</span>
        <span>conf {m.confidence != null ? `${Math.round(parseFloat(m.confidence)*100)}%` : '—'}</span>
        {m.source_tool && <span>via <code style={{ color: 'var(--primary-color)' }}>{m.source_tool}</code></span>}
        {m.is_consolidated && <span style={{ color: 'var(--warning-color)' }}>consolidated</span>}
        <span style={{ marginLeft: 'auto' }}>{m.updated_at ? new Date(m.updated_at).toLocaleString() : ''}</span>
      </div>
      <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{m.content}</div>
    </div>
  );
}

export default function AdminCorneliusEntities() {
  const [userId, setUserId]     = useState('13');
  const [type,   setType]       = useState('all');
  const [search, setSearch]     = useState('');
  const [entities, setEntities] = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');
  const [selected, setSelected] = useState(null);
  const [memories, setMemories] = useState([]);
  const [memLoad,  setMemLoad]  = useState(false);

  const fetchEntities = useCallback(async () => {
    if (!userId.trim()) { setEntities([]); return; }
    setLoading(true); setError('');
    try {
      const params = new URLSearchParams({ user_id: userId.trim(), limit: '200' });
      if (type !== 'all') params.set('type', type);
      const r = await fetch(`/api/opo/cornelius-memories/entities?${params}`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setEntities(data.entities || []);
    } catch (e) { setError(e.message); }
    setLoading(false);
  }, [userId, type]);

  useEffect(() => { fetchEntities(); }, [fetchEntities]);

  async function openEntity(e) {
    setSelected(e);
    setMemories([]);
    setMemLoad(true);
    try {
      const r = await fetch(`/api/opo/cornelius-memories/entities/${e.id}/memories?user_id=${userId}`);
      const data = await r.json();
      setMemories(data.memories || []);
    } catch (err) { setError(err.message); }
    setMemLoad(false);
  }

  const filtered = entities.filter(e =>
    !search.trim() || e.display_name.toLowerCase().includes(search.toLowerCase())
  );

  // Group by type
  const byType = {};
  for (const e of filtered) {
    (byType[e.entity_type] ||= []).push(e);
  }

  return (
    <div style={{ maxWidth: 1100 }}>
      <div style={{ marginBottom: 16, color: 'var(--text-secondary)', fontSize: 13 }}>
        Entities extracted from this user's memory stream (Tier-2 graph layer).
        Click any entity to see the memories that mention it.
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <input value={userId} onChange={e=>setUserId(e.target.value)} onKeyDown={e=>e.key==='Enter'&&fetchEntities()}
               placeholder="User ID" style={{ ...input, width: 100 }} />
        <select value={type} onChange={e=>setType(e.target.value)} style={{ ...input, cursor: 'pointer' }}>
          <option value="all">All types</option>
          {TYPE_ORDER.map(t => <option key={t} value={t}>{TYPE_LABELS[t]}</option>)}
        </select>
        <input value={search} onChange={e=>setSearch(e.target.value)}
               placeholder="Filter entity name…" style={{ ...input, flex: 1, minWidth: 220 }} />
        <button onClick={fetchEntities} style={btn}>Refresh</button>
      </div>

      {error   && <div style={{ color: 'var(--danger-color)', fontSize: 13, marginBottom: 12 }}>Error: {error}</div>}
      {loading && <div style={{ color: 'var(--text-secondary)' }}>Loading…</div>}

      <div style={{ display: 'grid', gridTemplateColumns: selected ? '1fr 1fr' : '1fr', gap: 16 }}>
        <div>
          {!loading && filtered.length === 0 && (
            <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
              No entities yet. Cornelius extracts them as new memories are written.
            </div>
          )}
          {TYPE_ORDER.filter(t => byType[t] && byType[t].length).map(t => (
            <div key={t} style={{ marginBottom: 20 }}>
              <h3 style={{ fontSize: 14, color: 'var(--text-secondary)', margin: '0 0 8px 2px', fontWeight: 600 }}>
                {TYPE_LABELS[t]} ({byType[t].length})
              </h3>
              {byType[t].map(e => (
                <EntityRow key={e.id} entity={e} selected={selected?.id === e.id} onClick={() => openEntity(e)} />
              ))}
            </div>
          ))}
        </div>

        {selected && (
          <div style={{
            background: 'var(--card-bg)', border: '1px solid var(--border-color)',
            borderRadius: 'var(--border-radius)', padding: 12, alignSelf: 'flex-start',
            position: 'sticky', top: 80, maxHeight: 'calc(100vh - 120px)', overflowY: 'auto',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <Pill color={TYPE_COLOR[selected.entity_type]}>{selected.entity_type}</Pill>
              <strong style={{ color: 'var(--text-primary)' }}>{selected.display_name}</strong>
              <button onClick={() => { setSelected(null); setMemories([]); }}
                      style={{ marginLeft: 'auto', ...btn, background: 'transparent',
                               color: 'var(--text-secondary)', border: '1px solid var(--border-color)',
                               padding: '4px 8px', fontSize: 12 }}>Close</button>
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 12 }}>
              {selected.mention_count} mention{selected.mention_count === 1 ? '' : 's'}
              {' · first '}{selected.first_seen ? new Date(selected.first_seen).toLocaleDateString() : '—'}
              {' · last '}{selected.last_seen ? new Date(selected.last_seen).toLocaleDateString() : '—'}
            </div>
            {memLoad && <div style={{ color: 'var(--text-secondary)' }}>Loading memories…</div>}
            {!memLoad && memories.length === 0 && (
              <div style={{ color: 'var(--text-secondary)', fontSize: 12 }}>No live memories linked.</div>
            )}
            {memories.map(m => <MemoryRow key={m.id} m={m} />)}
          </div>
        )}
      </div>
    </div>
  );
}
